import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminFetchBtwEmailTemplates,
  adminCreateBtwEmailTemplate,
  adminUpdateBtwEmailTemplate,
  adminArchiveBtwEmailTemplate,
} from "../../core/axios/endpoints";
import ConfirmModal from "../../components/confirm-modal";

const emptyForm = { name: "", subject: "", body: "", scope: "btw" };
const SCOPE_LABEL = { btw: "BTW Batterij", subsidie: "Subsidie (EIA, EIA Zon, ISDE)" };

const BtwEmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // template-object of "new"
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetchBtwEmailTemplates({
        includeArchived: showArchived,
      });
      setTemplates(res?.data ?? res ?? []);
    } catch (e) {
      console.error("Templates laden mislukt:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [showArchived]);

  const startNew = () => {
    setEditing("new");
    setForm(emptyForm);
    setError(null);
  };

  const startEdit = (tpl) => {
    setEditing(tpl._id);
    setForm({ name: tpl.name, subject: tpl.subject, body: tpl.body, scope: tpl.scope ?? "btw" });
    setError(null);
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyForm);
    setError(null);
  };

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      setError("Vul alle velden in.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing === "new") {
        await adminCreateBtwEmailTemplate(form);
      } else {
        await adminUpdateBtwEmailTemplate(editing, form);
      }
      await load();
      cancel();
    } catch (e) {
      setError(e?.response?.data?.error ?? e?.message ?? "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async () => {
    if (!archiveTarget) return;
    setSaving(true);
    try {
      await adminArchiveBtwEmailTemplate(
        archiveTarget._id,
        !archiveTarget.archived,
      );
      setArchiveTarget(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error ?? "Mislukt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper px-8 pt-20 pb-12">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
            <Link to="/panel/admin/dashboard" className="hover:underline">Dashboard</Link>
            <span className="mx-1">/</span>
            <span>BTW-teruggave</span>
            <span className="mx-1">/</span>
            <span>Mail-templates</span>
          </nav>
          <h1 className="text-2xl font-medium text-ink">
            Mail-templates
          </h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            Sjablonen die in de &quot;Mail naar klant&quot; modal verschijnen als
            quick-pick knoppen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex h-8 cursor-pointer items-center gap-2 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-line text-brand focus:ring-0"
            />
            Toon gearchiveerd
          </label>
          <button
            type="button"
            onClick={startNew}
            disabled={editing !== null}
            className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            + Nieuwe template
          </button>
        </div>
      </div>

      {/* Editor */}
      {editing !== null ? (
        <div className="mb-6 rounded bg-white p-5 shadow-card">
          <h3 className="mb-3 text-base font-semibold text-ink">
            {editing === "new" ? "Nieuwe template" : "Template bewerken"}
          </h3>
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="text-xs font-semibold text-ink-subtlest">Voor welke aanvragen</span>
              <select
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                disabled={saving}
                className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
              >
                <option value="btw">BTW Batterij</option>
                <option value="subsidie">Subsidie (EIA, EIA Zon, ISDE)</option>
              </select>
              <span className="mt-1 block text-xs text-ink-subtlest">Bepaalt in welke mail-modal de template verschijnt en welke variabelen beschikbaar zijn.</span>
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold text-ink-subtlest">
                Naam <span className="text-xs text-ink-subtlest">(quick-pick label)</span>
              </span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bijv. Aanvullende documenten nodig"
                className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
                disabled={saving}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold text-ink-subtlest">Onderwerp</span>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Onderwerp van de mail"
                className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
                disabled={saving}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold text-ink-subtlest">
                Bericht <span className="text-xs text-ink-subtlest">(lege regels = nieuwe alinea)</span>
              </span>
              <textarea
                rows={8}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Schrijf hier de standaard tekst. Gebruik [vul aan] als placeholder voor variabele delen."
                className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
                disabled={saving}
              />
            </label>
            {error ? (
              <div className="rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
                {error}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancel}
                disabled={saving}
                className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Lijst */}
      <div className="overflow-hidden rounded bg-white shadow-card">
        {loading ? (
          <div className="p-10 text-center text-sm text-ink-subtlest">Laden…</div>
        ) : templates.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink-subtlest">
            {showArchived ? "Geen templates." : "Geen actieve templates. Maak er een aan."}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {templates.map((tpl) => (
              <li key={tpl._id} className="flex items-start gap-4 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-ink">
                      {tpl.name}
                      <span className={`ml-2 inline-flex items-center rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide ${tpl.scope === "subsidie" ? "bg-[#F3F0FF] text-[#5E4DB2]" : "bg-[#E6FCFF] text-[#206A83]"}`}>
                        {SCOPE_LABEL[tpl.scope ?? "btw"]}
                      </span>
                    </h3>
                    {tpl.archived ? (
                      <span className="inline-flex items-center rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                        Gearchiveerd
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-ink-subtle">{tpl.subject}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-ink-subtlest">
                    {tpl.body}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(tpl)}
                    disabled={editing !== null}
                    className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Bewerken
                  </button>
                  <button
                    type="button"
                    onClick={() => setArchiveTarget(tpl)}
                    disabled={editing !== null}
                    className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {tpl.archived ? "Terughalen" : "Archiveren"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmModal
        isOpen={!!archiveTarget}
        title={archiveTarget?.archived ? "Terughalen?" : "Archiveren?"}
        message={
          archiveTarget?.archived
            ? `"${archiveTarget?.name}" wordt weer beschikbaar als quick-pick.`
            : `"${archiveTarget?.name}" verdwijnt uit de quick-picks. Bestaande mails blijven gewoon.`
        }
        confirmLabel={archiveTarget?.archived ? "Terughalen" : "Archiveren"}
        cancelLabel="Annuleren"
        busy={saving}
        onConfirm={doArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
};

export default BtwEmailTemplatesPage;
